import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FlightSegment } from '@appTypes/api.types';
import { SVGIcon } from '@data/svg-icons.data';
import { TranslatePipe } from '@ngx-translate/core';
import { airlineLogoIcon } from '@core/services/airline-logos.service';
import { FlightClassComponent } from '@shared/components/flight-class/flight-class.component';
import { RunFunctionPipe } from '@shared/pipes/run-function.pipe';
import { StopTimePipe } from '@shared/pipes/stop-time.pipe';
import { environment } from '@env/environment';

/** Calendar days between two date-times by their date part ("yyyy-MM-dd"; airport local times, no time zone applied). */
export function legCalendarDays(earlier: unknown, later: unknown): number {
  const day = (v: unknown): number | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v === null || v === undefined ? '' : String(v));
    return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86_400_000 : null;
  };
  const a = day(earlier);
  const b = day(later);
  return a === null || b === null ? 0 : b - a;
}

/**
 * Every leg of one direction of a flight on its own line (docs flows/tour-package.md R-TD10, features/website/cart.md R-C31 / R-C32): flight number,
 * airline logo, departure / arrival airport and city, `dd.MM.yyyy HH:mm` (C24), the stop between legs and a "+N" when a leg lands on a later day.
 * Shared by the flight segment popover (Details / results) and the cart's tour item, so both show the same leg layout.
 * Test ids (C16) come from the data: `<testIdPrefix>-<flightCode>-<ddMMyyyyHHmm of the departure>`, its "+N" `...-days`, a stop `<testIdPrefix>-stop-<index>`.
 */
@Component({
  selector: 'app-flight-leg-list',
  imports: [TranslatePipe, MatIconModule, FlightClassComponent, DatePipe, RunFunctionPipe, StopTimePipe],
  templateUrl: './flight-leg-list.component.html',
  styleUrl: './flight-leg-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlightLegListComponent {
  readonly segments = input<FlightSegment[] | null | undefined>([]);
  readonly isBlock = input<boolean | undefined>(false);
  readonly testIdPrefix = input('flight-leg');

  readonly dateTimeFormat = environment.formats.dateTime;

  readonly allSegments = computed(() => this.segments() ?? []);

  readonly legs = computed(() =>
    this.allSegments().map((segment) => ({
      segment,
      testId: `${this.testIdPrefix()}-${segment.flightCode ?? 'leg'}-${FlightLegListComponent.stamp(segment.departureFlightDate)}`,
      days: legCalendarDays(segment.departureFlightDate, segment.arrivalFlightDate)
    }))
  );

  /** ddMMyyyyHHmm of the departure as sent (airport local time). */
  static stamp(value: unknown): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(
      value === null || value === undefined ? '' : String(value)
    );
    return m ? `${m[3]}${m[2]}${m[1]}${m[4]}${m[5]}` : 'x';
  }

  /** R-FC38 (flight-catalogue qa.md): the airline's logo from NugiosCore, resolved by AirlineLogoIconRegistry. */
  getAirlineIconFn = (airlineId: string): SVGIcon => airlineLogoIcon(airlineId);
}
